import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PageHero } from "../components/common/PageHero";
import { Reveal } from "../components/common/Reveal";
import { api } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";

const PRODUCT_TYPES = [
    { key: "cotton_candy", label: "Cotton Candy", sub: "Soft, fresh-spun clouds" },
    { key: "popcorn", label: "Popcorn", sub: "Fresh popped, bold flavors" },
];

export const FlavorsPage = () => {
    usePageMeta("Flavors — Gourmet Cotton Candy & Popcorn Brooklyn | Showtime Carnival Co.",
        "Explore Showtime's cotton candy and popcorn flavor catalogs — classics, fruity, sweet, savory, seasonal and mystery spins.");
    const [flavors, setFlavors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [productType, setProductType] = useState("cotton_candy");
    const [active, setActive] = useState("");

    useEffect(() => {
        api.get("/flavors").then((res) => setFlavors(res.data || [])).catch(() => {});
        api.get("/flavor-categories").then((res) => setCategories(res.data || [])).catch(() => {});
    }, []);

    const productCats = useMemo(() => categories.filter((c) => c.product_type === productType), [categories, productType]);

    useEffect(() => {
        setActive(productCats[0]?.name || "");
    }, [productCats]);

    const visible = flavors.filter((f) => f.product_type === productType && f.category === active);

    return (
        <>
            <PageHero
                kicker="The Lineup"
                title="Every Flavor On The Bill."
                copy="Cotton candy and popcorn run separate menus — pick your treat first, then your flavor. The lineup rotates."
                dark={false}
                testid="flavors-hero"
            />
            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                    <Reveal>
                        <div className="grid sm:grid-cols-2 gap-4 mb-10 max-w-3xl" data-testid="flavors-product-toggle">
                            {PRODUCT_TYPES.map((pt) => (
                                <button
                                    key={pt.key}
                                    onClick={() => setProductType(pt.key)}
                                    data-testid={`flavors-product-${pt.key}`}
                                    className={`rounded-3xl p-6 text-left transition-all duration-300 active:scale-[0.98] ${
                                        productType === pt.key
                                            ? "bg-ink text-white ring-2 ring-pink"
                                            : "border border-zinc-200 bg-paper hover:border-pink"
                                    }`}
                                >
                                    <span className="font-oswald uppercase text-2xl block">{pt.label} Flavors</span>
                                    <span className={`text-xs mt-1 block ${productType === pt.key ? "text-zinc-300" : "text-zinc-500"}`}>{pt.sub}</span>
                                </button>
                            ))}
                        </div>
                        <div className="flex flex-wrap gap-2 mb-14" data-testid="flavors-tabs">
                            {productCats.map((cat) => (
                                <button
                                    key={cat.id}
                                    onClick={() => setActive(cat.name)}
                                    data-testid={`flavors-tab-${cat.name.toLowerCase()}`}
                                    className={`rounded-full px-5 py-2.5 font-oswald uppercase tracking-widest text-xs transition-colors duration-300 active:scale-95 ${
                                        active === cat.name ? "bg-pink text-ink" : "border border-zinc-300 text-zinc-600 hover:border-pink hover:text-pink-dark"
                                    }`}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                    </Reveal>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={`${productType}-${active}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
                            data-testid="flavors-grid"
                        >
                            {visible.map((f) => (
                                <article
                                    key={f.id}
                                    data-testid={`flavor-card-${f.id}`}
                                    className="group rounded-3xl border border-zinc-200 bg-white p-7 hover:border-pink transition-colors duration-300"
                                >
                                    {f.image ? (
                                        <img src={f.image} alt={f.name} className="h-20 w-20 rounded-2xl object-cover border border-zinc-200 mb-6 transition-transform duration-300 group-hover:scale-105" />
                                    ) : (
                                        <span
                                            className="block h-14 w-14 rounded-full border border-zinc-200 mb-6 transition-transform duration-300 group-hover:scale-110"
                                            style={{ background: f.color }}
                                        />
                                    )}
                                    <h2 className="font-oswald uppercase text-2xl">
                                        {f.name}
                                        {f.seasonal && <span className="ml-2 align-middle text-[9px] tracking-[0.3em] text-pink-dark border border-pink rounded-full px-2 py-0.5">SEASONAL</span>}
                                    </h2>
                                    <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{f.description}</p>
                                </article>
                            ))}
                            {visible.length === 0 && (
                                <p className="col-span-full text-zinc-500 py-10 font-oswald uppercase tracking-widest">New {active.toLowerCase()} flavors are being spun up.</p>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </section>
        </>
    );
};
